import { useLazyQuery } from '@apollo/client';
import { transformExitRulesResponse } from 'modules/journey/components/ExitRules/ExitRules.utils';
import { ReadOnlyExitRules } from 'modules/journey/components/ExitRules/ReadOnlyExitRules';
import { fetchExitRulesQuery } from 'modules/journey/components/ExitRules/api/exitRulesQueries';
import { ExitRulesResponse } from 'modules/journey/components/ExitRules/types';
import {
  RuleBuilderProvider,
  useRuleBuilder,
} from 'modules/journey/components/RuleBuilder/hooks/useRuleBuilder';
import { Rules } from 'modules/journey/components/RuleBuilder/types';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MODAL_SIZES, MODAL_VARIANTS, Modal, ModalBody, ModalFooter, ModalHeader } from 'ui/modal';
import { READ_ONLY_EXIT_RULES_MODAL } from '../../../constants';
import { closeModal, getWorkflow } from '../../../ducks';

export const SAVE_EXIT_RULES_BUTTON_TEST_ID = 'save-exit-rules-button';
export const DRAFT_EXIT_RULES_BANNER = 'draft-exit-rules-banner';
export const PUBLISHED_EXIT_RULES_BANNER = 'published-exit-rules-banner';

export type ReadOnlyExitRulesModalProps = {
  /** Whether the modal is open */
  isOpen: boolean;
};

export const ReadOnlyExitRulesModalView: React.FC<
  React.PropsWithChildren<ReadOnlyExitRulesModalProps>
> = ({ isOpen }) => {
  const { isLoading } = useRuleBuilder();
  const dispatch = useDispatch();
  const handleCloseModal = () => dispatch(closeModal(READ_ONLY_EXIT_RULES_MODAL));

  return (
    <Modal
      id={READ_ONLY_EXIT_RULES_MODAL}
      data-test={`modal-${READ_ONLY_EXIT_RULES_MODAL}`}
      isModalOpen={isOpen}
      onClose={handleCloseModal}
      size={MODAL_SIZES.LARGE}
      variant={MODAL_VARIANTS.DEFAULT}
    >
      <ModalHeader autoFocus={false}>Users exited by rules</ModalHeader>
      <ModalBody>{!isLoading ? <ReadOnlyExitRules /> : <div>Loading</div>}</ModalBody>
      <ModalFooter hasCancel={false}></ModalFooter>
    </Modal>
  );
};

export const ReadOnlyExitRulesModal: React.FC<
  React.PropsWithChildren<ReadOnlyExitRulesModalProps>
> = ({ isOpen }) => {
  const workflow = useSelector(getWorkflow);

  const [fetchExitRules, { loading: isLoading, data: exitRulesQueryData }] = useLazyQuery(
    fetchExitRulesQuery,
    {
      fetchPolicy: 'no-cache',
      variables: {
        workflowId: workflow?.id,
        workflowType: workflow?.workflowType,
        includeArchived: true,
      },
    },
  );

  const [exitRules, setExitRules] = useState<Rules>([]);

  useEffect(() => {
    if (exitRulesQueryData?.workflow?.exitRules?.length) {
      const rules = transformExitRulesResponse(
        exitRulesQueryData?.workflow as ExitRulesResponse,
      )?.rules;
      setExitRules(rules);
    }
  }, [exitRulesQueryData]);

  useEffect(() => {
    if (workflow?.id) fetchExitRules();
  }, [workflow]);

  return (
    <RuleBuilderProvider rules={exitRules} isClonedWithErrors={false} isLoading={isLoading}>
      <ReadOnlyExitRulesModalView isOpen={isOpen} />
    </RuleBuilderProvider>
  );
};
